import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BadgePercent, Loader2, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { formatDate, formatRupiah } from "../lib/format";
import { salesApi } from "../lib/api";

const emptyForm = {
  id: "",
  code: "",
  name: "",
  description: "",
  discount_type: "percentage",
  discount_target: "product",
  value: "",
  max_discount: "",
  min_order_amount: "0",
  is_active: true,
};

const AdminCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await salesApi.getDiscounts();
      setCoupons(data || []);
    } catch (error) {
      toast.error(error.message || "Gagal memuat kupon");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return coupons;
    return coupons.filter((coupon) =>
      [coupon.code, coupon.name, coupon.description].filter(Boolean).some((value) => value.toLowerCase().includes(q)),
    );
  }, [coupons, search]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const openNew = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (coupon) => {
    setForm({
      id: coupon.id,
      code: coupon.code || "",
      name: coupon.name || "",
      description: coupon.description || "",
      discount_type: coupon.discount_type || "percentage",
      discount_target: coupon.discount_target || "product",
      value: String(coupon.value ?? ""),
      max_discount: coupon.max_discount ? String(coupon.max_discount) : "",
      min_order_amount: String(coupon.min_order_amount ?? 0),
      is_active: coupon.is_active !== false,
    });
    setOpen(true);
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (!form.code.trim()) return toast.error("Kode kupon wajib diisi");
    if (!form.name.trim()) return toast.error("Nama kupon wajib diisi");
    if (!form.value || Number(form.value) <= 0) return toast.error("Nilai diskon harus lebih dari 0");
    if (form.discount_type === "percentage" && Number(form.value) > 100) {
      return toast.error("Diskon persentase maksimal 100%");
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      discount_type: form.discount_type,
      discount_target: form.discount_target,
      value: Number(form.value),
      max_discount: form.max_discount ? Number(form.max_discount) : null,
      min_order_amount: Number(form.min_order_amount || 0),
      is_active: form.is_active,
    };

    try {
      setSaving(true);
      if (form.id) {
        await salesApi.updateDiscount(form.id, payload);
        toast.success("Kupon berhasil diperbarui");
      } else {
        await salesApi.createDiscount(payload);
        toast.success("Kupon berhasil dibuat");
      }
      setOpen(false);
      await loadCoupons();
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan kupon");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (coupon) => {
    try {
      await salesApi.updateDiscountStatus(coupon.id, !coupon.is_active);
      toast.success(`Kupon ${!coupon.is_active ? "diaktifkan" : "dinonaktifkan"}`);
      await loadCoupons();
    } catch (error) {
      toast.error(error.message || "Gagal mengubah status kupon");
    }
  };

  const deleteCoupon = async (coupon) => {
    try {
      await salesApi.deleteDiscount(coupon.id);
      toast.success("Kupon berhasil dihapus");
      await loadCoupons();
    } catch (error) {
      toast.error(error.message || "Gagal menghapus kupon");
    }
  };

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="admin-coupons-page">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <BadgePercent className="h-6 w-6" />
            </span>
            <div>
              <h1 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">Kupon</h1>
              <p className="mt-1 text-base text-stone-600">Kelola promo, kode diskon, dan tracking pemakaian kupon.</p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                <Plus className="mr-2 h-5 w-5" /> Tambah Kupon
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{form.id ? "Edit Kupon" : "Tambah Kupon"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSave} className="space-y-5 pt-2">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Kode Kupon</Label>
                    <Input value={form.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} placeholder="DISCIDULADHA" className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label>Nama Promo</Label>
                    <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Diskon Idul Adha" className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label>Tipe Diskon</Label>
                    <Select value={form.discount_type} onValueChange={(value) => setField("discount_type", value)}>
                      <SelectTrigger className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Persentase</SelectItem>
                        <SelectItem value="fixed">Nominal Tetap</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Potong Dari</Label>
                    <Select value={form.discount_target} onValueChange={(value) => setField("discount_target", value)}>
                      <SelectTrigger className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Subtotal Produk</SelectItem>
                        <SelectItem value="shipping">Ongkir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{form.discount_type === "percentage" ? "Nilai Diskon (%)" : "Nilai Diskon (Rp)"}</Label>
                    <Input type="number" min="0" value={form.value} onChange={(e) => setField("value", e.target.value)} className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label>Maksimal Diskon (Rp)</Label>
                    <Input type="number" min="0" value={form.max_discount} onChange={(e) => setField("max_discount", e.target.value)} placeholder="Opsional" className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label>Minimum Belanja (Rp)</Label>
                    <Input type="number" min="0" value={form.min_order_amount} onChange={(e) => setField("min_order_amount", e.target.value)} className="h-12 rounded-xl" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Deskripsi</Label>
                    <Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Catatan internal campaign promo" className="min-h-24 rounded-xl" />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 p-4">
                    <div>
                      <p className="font-medium text-stone-900">Status Kupon</p>
                      <p className="text-sm text-stone-500">Kupon aktif bisa dipakai pada transaksi baru.</p>
                    </div>
                    <Switch checked={form.is_active} onCheckedChange={(value) => setField("is_active", value)} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-xl">Batal</Button>
                  <Button type="submit" disabled={saving} className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Kupon
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari kode atau nama kupon..." className="h-12 rounded-xl pl-11" />
          </div>
          <Button variant="outline" onClick={loadCoupons} className="h-12 rounded-xl">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center text-stone-500">
            <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" /> Memuat kupon...
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm md:block">
              <table className="w-full min-w-[920px] text-left">
              <thead className="bg-stone-50 text-sm text-stone-500">
                <tr>
                  <th className="px-5 py-4 font-medium">Kupon</th>
                  <th className="px-5 py-4 font-medium">Diskon</th>
                  <th className="px-5 py-4 font-medium">Pemakaian</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Dibuat</th>
                  <th className="px-5 py-4 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((coupon) => (
                  <tr key={coupon.id}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-stone-900">{coupon.code}</p>
                      <p className="text-sm text-stone-500">{coupon.name}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-stone-900">
                        {coupon.discount_type === "percentage" ? `${coupon.value}%` : formatRupiah(coupon.value)}
                      </p>
                      <p className="text-sm text-stone-500">
                        {coupon.discount_target === "shipping" ? "Potong ongkir" : "Potong produk"} � Min. {formatRupiah(coupon.min_order_amount)}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-stone-900">{coupon.usage_count} transaksi</p>
                      <p className="text-sm text-rose-600">-{formatRupiah(coupon.total_discount_amount)}</p>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => toggleStatus(coupon)}
                        className={`rounded-full px-3 py-1 text-xs font-medium ${coupon.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}
                      >
                        {coupon.is_active ? "Aktif" : "Nonaktif"}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-sm text-stone-500">{formatDate(coupon.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-2">
                        <Button variant="outline" onClick={() => openEdit(coupon)} className="h-10 rounded-lg">
                          <Pencil className="mr-1 h-4 w-4" /> Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="h-10 rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50">
                              <Trash2 className="mr-1 h-4 w-4" /> Hapus
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus kupon {coupon.code}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Kupon yang sudah dipakai transaksi tidak akan bisa dihapus. Gunakan nonaktif jika ingin menjaga histori.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteCoupon(coupon)} className="bg-rose-600 hover:bg-rose-700">
                                Ya, Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <div className="space-y-3 md:hidden">
              {filtered.map((coupon) => (
                <div key={coupon.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-stone-900">{coupon.code}</p>
                      <p className="text-sm text-stone-500">{coupon.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleStatus(coupon)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${coupon.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}
                    >
                      {coupon.is_active ? "Aktif" : "Nonaktif"}
                    </button>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <p className="font-medium text-stone-900">
                      {coupon.discount_type === "percentage" ? `${coupon.value}%` : formatRupiah(coupon.value)}
                    </p>
                    <p className="text-stone-500">
                      {coupon.discount_target === "shipping" ? "Potong ongkir" : "Potong produk"} � Min. {formatRupiah(coupon.min_order_amount)}
                    </p>
                    <p className="text-stone-700">{coupon.usage_count} transaksi</p>
                    <p className="text-rose-600">-{formatRupiah(coupon.total_discount_amount)}</p>
                    <p className="text-xs text-stone-500">Dibuat {formatDate(coupon.created_at)}</p>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" onClick={() => openEdit(coupon)} className="h-10 flex-1 rounded-lg">
                      <Pencil className="mr-1 h-4 w-4" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="h-10 flex-1 rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50">
                          <Trash2 className="mr-1 h-4 w-4" /> Hapus
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus kupon {coupon.code}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Kupon yang sudah dipakai transaksi tidak akan bisa dihapus. Gunakan nonaktif jika ingin menjaga histori.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteCoupon(coupon)} className="bg-rose-600 hover:bg-rose-700">
                            Ya, Hapus
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </AdminLayout>
  );
};

export default AdminCouponsPage;

